// Per-user Dashboard Tool Visibility — admin hides tools for specific users
import React, { useCallback, useEffect, useState } from 'react';
import { Search, Eye, EyeOff, Save, Music, Upload } from 'lucide-react';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

export default function AdminUserTools() {
  const [tools, setTools] = useState([]);
  const [labels, setLabels] = useState({});
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toneUploading, setToneUploading] = useState(false);
  const [mode, setMode] = useState('hide'); // 'hide' = click to hide (default) · 'show_only' = click to keep visible
  // Wave 47: in show_only mode we track the KEPT-visible set separately.
  // On save (show_only), we invert: hidden = tools \ kept.
  const [kept, setKept] = useState([]);

  const loadTools = useCallback(async () => {
    try { const r = await api.get('/admin/user-tools/known'); setTools(r.data.tools || []); setLabels(r.data.labels || {}); } catch {}
  }, []);
  useEffect(() => { loadTools(); }, [loadTools]);

  const search = async () => {
    try {
      const r = await api.get('/admin/user-tools/search', { params: { q, limit: 30 } });
      setUsers(r.data.users || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Search failed'); }
  };

  const pick = (u) => {
    setSelected(u);
    setHidden(u.hidden_tools || []);
    setKept([]);
    setMode('hide');
  };

  const uploadTone = async (e) => {
    const f = e.target.files?.[0]; if (!f || !selected) return;
    setToneUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/upload/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.url) {
        await api.post(`/admin/user-tools/user/${selected.id}/alert-tone`, { alert_tone_url: r.data.url });
        toast.success('🔔 Alert tone saved');
        setSelected({ ...selected, alert_tone_url: r.data.url });
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setToneUploading(false); e.target.value = ''; }
  };

  const clearTone = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/user-tools/user/${selected.id}/alert-tone`, { alert_tone_url: null });
      toast.success('Reset to default chime'); setSelected({ ...selected, alert_tone_url: null });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggle = (t) => setHidden((s) => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
  const toggleKept = (t) => setKept((s) => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
  // Wave 47: when user toggles into show_only mode, bootstrap `kept` from current visibility.
  const switchMode = (m) => {
    if (m === 'show_only' && mode !== 'show_only') {
      setKept(tools.filter((t) => !hidden.includes(t)));
    }
    setMode(m);
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      // Wave 47: In show_only mode, hidden = all tools MINUS kept (auto-hide the rest).
      const payloadHidden = mode === 'show_only'
        ? tools.filter((t) => !kept.includes(t))
        : hidden;
      await api.post(`/admin/user-tools/user/${selected.id}`, { hidden_tools: payloadHidden });
      // Re-read from the DB so the grid reflects what was actually persisted.
      const r = await api.get(`/admin/user-tools/user/${selected.id}`);
      setSelected({ ...selected, hidden_tools: r.data.hidden_tools });
      setHidden(r.data.hidden_tools || []);
      // Reset show_only staging so the UI reflects the saved reality.
      if (mode === 'show_only') setKept(tools.filter((t) => !(r.data.hidden_tools || []).includes(t)));
      toast.success(`✅ Saved. ${(r.data.hidden_tools || []).length} tool(s) hidden for ${selected.username || selected.email}`);
      search();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="admin-user-tools">
        <div>
          <h1 className="text-2xl md:text-3xl font-black">👁️ Per-user Dashboard Tool Visibility</h1>
          <p className="text-xs opacity-70">Hide specific tools for specific users · They'll only see what you allow</p>
        </div>

        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
                 placeholder="Search by username, email, or name" data-testid="user-tools-search-input"
                 className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <button onClick={search} data-testid="user-tools-search-btn"
                  className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1">
            <Search size={12} /> Search
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-2 space-y-1 max-h-96 overflow-y-auto">
            {users.length === 0 && <div className="text-center py-6 opacity-60 text-xs">Search for users</div>}
            {users.map((u) => (
              <button key={u.id} onClick={() => pick(u)} data-testid={`user-tools-pick-${u.username || u.id.slice(0,8)}`}
                      className={`w-full text-left p-2 rounded-lg text-xs transition ${selected?.id === u.id ? 'bg-emerald-500/20 border border-emerald-400' : 'bg-slate-800/50 hover:bg-slate-800 border border-transparent'}`}>
                <div className="font-black truncate">{u.name} <span className="opacity-60">@{u.username}</span></div>
                <div className="text-[10px] opacity-70 truncate">{u.email}</div>
                {u.hidden_tools_count > 0 && (
                  <div className="text-[10px] text-rose-300 mt-0.5">🚫 {u.hidden_tools_count} tool(s) hidden</div>
                )}
              </button>
            ))}
          </div>

          <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-3 space-y-2">
            {!selected && <div className="text-center py-10 opacity-60 text-xs">Select a user to manage tools</div>}
            {selected && (
              <>
                <div className="text-sm font-black">Managing: {selected.name} (@{selected.username})</div>
                <div className="p-2 rounded-lg bg-slate-800/50 space-y-2">
                  <div className="flex items-center gap-2 text-[10px] font-black">
                    <span>MODE:</span>
                    <button onClick={() => switchMode('hide')} data-testid="mode-hide"
                            className={`px-2 py-0.5 rounded ${mode === 'hide' ? 'bg-rose-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                      Hide Selected
                    </button>
                    <button onClick={() => switchMode('show_only')} data-testid="mode-show-only"
                            className={`px-2 py-0.5 rounded ${mode === 'show_only' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-400'}`}>
                      Show ONLY Selected
                    </button>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setHidden([]); setKept(tools); }} data-testid="quick-show-all"
                            className="flex-1 px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-black">👁️ Show All</button>
                    <button onClick={() => { setHidden([...tools]); setKept([]); }} data-testid="quick-hide-all"
                            className="flex-1 px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black">🚫 Hide All</button>
                  </div>
                  <div className="text-[10px] opacity-80 text-center">
                    {mode === 'show_only' ? (
                      <><b className="text-emerald-300">{kept.length}</b> kept · <b className="text-rose-300">{tools.length - kept.length}</b> auto-hidden</>
                    ) : (
                      <><b className="text-emerald-300">{tools.length - hidden.length}</b> visible · <b className="text-rose-300">{hidden.length}</b> hidden</>
                    )}
                    <div className="text-[9px] opacity-60 mt-0.5">
                      {mode === 'hide'
                        ? '🚫 Click a tool = HIDE it for this user'
                        : '✅ Click a tool = KEEP visible · rest auto-hidden'}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto">
                  {tools.map((t) => {
                    const showOnlyActive = mode === 'show_only';
                    // In show_only: green if in kept, red otherwise. In hide: green if not in hidden, red otherwise.
                    const displayHidden = showOnlyActive ? !kept.includes(t) : hidden.includes(t);
                    const onClick = () => (showOnlyActive ? toggleKept(t) : toggle(t));
                    return (
                      <button key={t} onClick={onClick} data-testid={`user-tools-toggle-${t}`} title={t}
                              className={`px-2 py-1.5 rounded-lg text-[11px] font-black flex items-center justify-between gap-1 transition ${
                                displayHidden ? 'bg-rose-600/40 border border-rose-500 text-rose-200' : 'bg-emerald-500/20 border border-emerald-400 text-emerald-200'
                              }`}>
                        <span className="truncate">{labels[t] ? <>{labels[t]} <span className="opacity-60">· {t}</span></> : t}</span>
                        {displayHidden ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                    );
                  })}
                </div>
                <button onClick={save} disabled={busy} data-testid="user-tools-save"
                        className="w-full py-2 rounded-lg bg-yellow-400 text-slate-900 font-black text-sm flex items-center justify-center gap-1 disabled:opacity-50">
                  <Save size={14} /> Save Visibility ({mode === 'show_only' ? tools.length - kept.length : hidden.length} hidden)
                </button>

                {/* Custom alert tone uploader */}
                <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                  <div className="text-[11px] font-black opacity-80 flex items-center gap-1"><Music size={11} /> Custom Alert Tone (Smart Panel)</div>
                  {selected.alert_tone_url && (
                    <div className="flex items-center gap-2 p-2 rounded bg-slate-800/60">
                      <audio src={selected.alert_tone_url.startsWith('http') ? selected.alert_tone_url : `${process.env.REACT_APP_BACKEND_URL || ''}${selected.alert_tone_url}`} controls className="flex-1 h-8" data-testid="user-tools-tone-preview" />
                      <button onClick={clearTone} data-testid="user-tools-tone-clear" className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black">Clear</button>
                    </div>
                  )}
                  <label data-testid="user-tools-tone-upload"
                         className="flex items-center justify-center gap-1 py-1.5 rounded bg-slate-800 border border-dashed border-slate-600 hover:border-cyan-400 cursor-pointer text-[10px] font-black">
                    {toneUploading ? '⏳ Uploading…' : <><Upload size={11} /> Upload mp3/wav/ogg (≤5MB)</>}
                    <input type="file" accept="audio/*" onChange={uploadTone} className="hidden" disabled={toneUploading} />
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
